import { AxiosResponse } from 'axios'
import debounce from 'lodash.debounce'
import { useEffect, useState } from 'react'

import { Select, SelectProps } from '../Select/Select'
import { Option } from '../Select/Select'

import { EntityPage, Paging } from 'types/paging'

import { useAppErrorContext } from 'contexts/AppErrorContext'

export interface AsyncSelectProps<T extends { id: number; name: string | null }> extends Omit<SelectProps, 'options'> {
  getData: (params: Paging) => Promise<AxiosResponse<EntityPage<T>, any>>
  customOptionsHandle?: (data: T[]) => Option[]
  firstOption: Option
}

export function SelectAsync<T extends { id: number; name: string | null }>({
  getData,
  customOptionsHandle,
  firstOption,
  ...selectProps
}: AsyncSelectProps<T>) {
  const { setAppError } = useAppErrorContext()
  const [loading, setLoading] = useState(true)
  const [options, setOptions] = useState<Option[]>([])

  const promiseOptions = debounce((inputValue: string | null) => {
    getData({
      page: {
        page_number: 0,
        size: 10,
      },
      order_by: 'created_at',
      direction: 'desc',
      filters: [],
      quick_search: inputValue || null,
    })
      .then((res) => {
        const items = res?.data?.data
        if (customOptionsHandle) {
          setOptions([firstOption, ...customOptionsHandle(items)])
        } else {
          setOptions([firstOption, ...items?.map((item: T) => ({ value: item.id, label: item?.name || '' }))])
        }
      })
      .catch((e: any) => {
        setAppError(e)
        setOptions([firstOption])
      })
      .finally(() => setLoading(false))
  }, 400)

  useEffect(() => {
    setLoading(true)
    promiseOptions(null)
  }, [])

  return (
    <Select
      {...selectProps}
      options={options}
      customInputChange={(e) => {
        setLoading(true)
        promiseOptions(e.target.value)
      }}
      loading={loading}
    />
  )
}
